/** @format */
import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router-dom"; // Added useLocation
import { motion } from "framer-motion";
import { Ruler, Info, HelpCircle } from "lucide-react";
import useDebounce from "../../hooks/useDebounce";
import { customizeProduct } from "../../actions/blindActions";
import { shadeTypeDefaults } from "./data"; // Assuming data file exists
import Customize_Reworked from ".";

// Constant for fraction options
const fraction = [
	"0",
	"1/16",
	"1/8",
	"3/16",
	"1/4",
	"5/16",
	"3/8",
	"7/16",
	"1/2",
	"9/16",
	"5/8",
	"11/16",
	"3/4",
	"13/16",
	"7/8",
	"15/16",
];

const Customize_Details = () => {
	// --- Redux and Router Hooks ---
	const dispatch = useDispatch();
	const location = useLocation(); // Get location object

	// --- Redux State ---
	// Destructure carefully, providing default values to prevent errors if state is initially undefined
	const { productInfo = {}, optionInfo = {} } = useSelector(
		(state) => state?.customize?.customizeProduct || {}
	);

	// --- Component State ---
	// Initialize state with values from Redux store or sensible defaults
	const [shadeWidth, setShadeWidth] = useState(optionInfo?.widthWhole || "");
	const [shadeWidthFrac, setShadeWidthFrac] = useState(
		optionInfo?.widthFraction || "0"
	);
	const [shadeLength, setShadeLength] = useState(optionInfo?.lengthWhole || "");
	const [shadeLengthFrac, setShadeLengthFrac] = useState(
		optionInfo?.lengthFraction || "0"
	);
	const [measure, setMeasure] = useState(optionInfo?.measure || "Tight"); // Default to 'Tight' if undefined

	// --- Debounced Values ---
	const debouncedWidth = useDebounce(shadeWidth, 300);
	const debouncedLength = useDebounce(shadeLength, 300);

	// --- URL Params ---
	// Use useMemo or handle directly in useEffect to avoid re-parsing on every render
	const params = new URLSearchParams(location.search);
	const defaultConfig = params.get("default");
	const fabricGroup = params.get("fabric"); // Keep if needed by dispatch logic, though not used directly in this component's UI

	// --- useEffect Hooks ---

	// Dispatch updates when debounced dimensions or other selections change
	useEffect(() => {
		// Don't dispatch if a default config is being loaded initially
		if (!defaultConfig) {
			// Basic validation before dispatching
			if (parseFloat(debouncedWidth) > 0 && parseFloat(debouncedLength) > 0) {
				dispatchDetails();
			} else {
				// Optionally dispatch an error state or handle invalid input display
				console.warn("Dimensions must be positive numbers.");
			}
		}
		// Dependencies: Include all state variables that should trigger a re-dispatch
	}, [
		debouncedWidth,
		debouncedLength,
		shadeWidthFrac,
		shadeLengthFrac,
		measure,
		defaultConfig,
	]); // Added defaultConfig to prevent dispatch during load

	// Handle loading default configuration from URL parameter
	useEffect(() => {
		if (defaultConfig && shadeTypeDefaults[defaultConfig]) {
			// Check if defaultConfig exists in data
			const defaults = shadeTypeDefaults[defaultConfig];
			console.log("Loading defaults for:", defaultConfig, defaults);

			// Prepare the default product info, including the fabric type from URL
			const defaultProductInfo = {
				...defaults.product,
				// Ensure Fabric_Type and Default_Fabric_Type are correctly set based on URL or defaults data
				Fabric_Type: fabricGroup || defaults.product?.Fabric_Type || "All", // Use URL param, then default, then 'All'
				Default_Fabric_Type:
					fabricGroup || defaults.product?.Default_Fabric_Type || "All",
			};

			dispatch(
				customizeProduct({
					productInfo: defaultProductInfo,
					optionInfo: defaults.option,
				})
			);

			// Update local component state to match the loaded defaults
			setShadeWidth(defaults.option.widthWhole);
			setShadeWidthFrac(defaults.option.widthFraction);
			setShadeLength(defaults.option.lengthWhole);
			setShadeLengthFrac(defaults.option.lengthFraction);
			setMeasure(defaults.option.measure);

			// Remove the 'default' query parameter from the URL without reloading the page
			// This uses the History API. Consider using react-router's navigate if preferred.
			const currentUrl = new URL(window.location);
			currentUrl.searchParams.delete("default");
			window.history.replaceState({}, document.title, currentUrl.toString());
		} else if (defaultConfig) {
			// Handle case where the default config key is invalid
			console.error(`Invalid default configuration key: ${defaultConfig}`);
			// Optionally redirect or show an error
			const currentUrl = new URL(window.location);
			currentUrl.searchParams.delete("default");
			window.history.replaceState({}, document.title, currentUrl.toString());
		}
	}, [defaultConfig, fabricGroup, dispatch]); // Dependencies for default loading logic

	// --- Dispatch Function ---
	const dispatchDetails = () => {
		// Ensure productInfo exists before spreading
		if (!productInfo) {
			console.error("Cannot dispatch details: productInfo is missing.");
			return;
		}
		dispatch(
			customizeProduct({
				// Preserve existing productInfo, only update relevant fields if necessary
				// Ensure error/loaded flags are managed correctly elsewhere or removed if not needed here
				productInfo: {
					...productInfo,
					// Program might be better set initially or based on Type, not here
					// Program: `Customized ${productInfo?.Type} Shade`,
				},
				// Update optionInfo with current component state
				optionInfo: {
					...optionInfo,
					widthWhole: shadeWidth,
					widthFraction: shadeWidthFrac,
					lengthWhole: shadeLength,
					lengthFraction: shadeLengthFrac,
					measure: measure,
				},
			})
		);
	};

	// --- Animation Variants ---
	const inputVariants = {
		focus: {
			scale: 1.01,
			borderColor: "rgb(var(--color-primary) / 0.5)",
			boxShadow: "0 0 0 2px rgb(var(--color-primary) / 0.2)",
		}, // Using Tailwind's primary color variable format
		blur: { scale: 1, borderColor: "#d1d5db", boxShadow: "none" }, // Tailwind gray-300
	};

	// --- Component Render ---
	return (
		<Customize_Reworked>
			<div className="space-y-8">
				{/* --- Size Input Section --- */}
				<fieldset className="space-y-4">
					{/* Section Header */}
					<legend className="text-lg font-medium mb-3 pb-2 border-b border-gray-200 flex items-center justify-between gap-3">
						<div className="flex items-center gap-2">
							<div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
								<Ruler className="w-5 h-5 text-primary" />
							</div>
							Enter Dimensions (inches)
						</div>
						{/* Help Link */}
						<Link
							to="/guides/measuring-guide"
							className="text-primary text-xs hover:underline ml-auto inline-flex items-center gap-1 flex-shrink-0"
							target="_blank" // Open in new tab
							rel="noopener noreferrer"
						>
							<HelpCircle size={14} /> Need Help Measuring?
						</Link>
					</legend>

					{/* Width & Length Input Grid */}
					<div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
						{/* Width Inputs */}
						<div className="space-y-2">
							<label
								htmlFor="width-whole"
								className="block text-sm font-medium text-gray-700"
							>
								Width
							</label>
							<div className="grid grid-cols-2 gap-3">
								{/* Width Whole Number */}
								<motion.div
									variants={inputVariants}
									whileFocus="focus"
									animate="blur"
									transition={{ duration: 0.15 }}
									className="relative"
								>
									<input
										id="width-whole"
										aria-label="Width Whole Inches"
										type="number"
										min="10" // Example: Set appropriate min/max based on product constraints
										max="120"
										step="1"
										value={shadeWidth}
										onChange={(e) => setShadeWidth(e.target.value)}
										className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary outline-none transition-all duration-150" // Simplified focus
										placeholder="e.g., 36"
										required
									/>
									<span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
										in
									</span>
								</motion.div>
								{/* Width Fraction */}
								<motion.div
									variants={inputVariants}
									whileFocus="focus"
									animate="blur"
									transition={{ duration: 0.15 }}
								>
									<label htmlFor="width-frac" className="sr-only">
										Width Fraction
									</label>
									<select
										id="width-frac"
										aria-label="Width Fraction"
										value={shadeWidthFrac}
										onChange={(e) => setShadeWidthFrac(e.target.value)}
										className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary outline-none appearance-none transition-all duration-150" // Simplified focus
									>
										{fraction.map((frac) => (
											<option key={`w-${frac}`} value={frac}>
												{frac}"
											</option>
										))}
									</select>
								</motion.div>
							</div>
							{/* Width Validation/Info Message (Example) */}
							{/* <p className="text-xs text-gray-500">Min width: 10", Max width: 120"</p> */}
						</div>

						{/* Length Inputs */}
						<div className="space-y-2">
							<label
								htmlFor="length-whole"
								className="block text-sm font-medium text-gray-700"
							>
								Length
							</label>
							<div className="grid grid-cols-2 gap-3">
								{/* Length Whole Number */}
								<motion.div
									variants={inputVariants}
									whileFocus="focus"
									animate="blur"
									transition={{ duration: 0.15 }}
									className="relative"
								>
									<input
										id="length-whole"
										aria-label="Length Whole Inches"
										type="number"
										min="12" // Example
										max="120"
										step="1"
										value={shadeLength}
										onChange={(e) => setShadeLength(e.target.value)}
										className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary outline-none transition-all duration-150"
										placeholder="e.g., 48"
										required
									/>
									<span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none">
										in
									</span>
								</motion.div>
								{/* Length Fraction */}
								<motion.div
									variants={inputVariants}
									whileFocus="focus"
									animate="blur"
									transition={{ duration: 0.15 }}
								>
									<label htmlFor="length-frac" className="sr-only">
										Length Fraction
									</label>
									<select
										id="length-frac"
										aria-label="Length Fraction"
										value={shadeLengthFrac}
										onChange={(e) => setShadeLengthFrac(e.target.value)}
										className="w-full px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg focus:ring-0 focus:border-primary outline-none appearance-none transition-all duration-150"
									>
										{fraction.map((frac) => (
											<option key={`l-${frac}`} value={frac}>
												{frac}"
											</option>
										))}
									</select>
								</motion.div>
							</div>
							{/* Length Validation/Info Message (Example) */}
							{/* <p className="text-xs text-gray-500">Min length: 12", Max length: 120"</p> */}
						</div>
					</div>
				</fieldset>

				{/* --- Mount Type Section --- */}
				<fieldset className="space-y-4 pt-4">
					{/* Section Header */}
					<legend className="text-lg font-medium mb-3 pb-2 border-b border-gray-200 flex items-center gap-2">
						<div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
							<Info className="w-5 h-5 text-primary" />
						</div>
						Select Mount Type
					</legend>
					{/* Mount Info Explanation */}
					<p className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg border border-blue-200">
						Choose <strong>Inside Mount</strong> to install within the window
						frame (we automatically deduct 1/8" from the width for clearance).
						Choose <strong>Outside Mount</strong> (Finish or Cloth) to install
						outside/above the frame (we make the shade to your exact specified
						width).{" "}
						<Link
							to="/guides/measuring-guide"
							className="text-primary hover:underline font-medium"
							target="_blank"
							rel="noopener noreferrer"
						>
							Learn more about mount types.
						</Link>
					</p>
					{/* Mount Type Options Grid */}
					<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
						{[
							{
								id: "Tight",
								label: "Inside Mount",
								desc: 'Fits inside frame (1/8" auto-deduction)',
								img: "/images/customizeToolRenders/Inside.png",
							},
							{
								id: "Finish",
								label: "Outside Mount (Finish Size)",
								desc: "Covers frame, shade is exact width",
								img: "/images/customizeToolRenders/Outside.png",
							},
							{
								id: "Cloth",
								label: "Outside Mount (Cloth Size)",
								desc: "Covers frame, fabric is exact width",
								img: "/images/customizeToolRenders/fabric.png",
							},
						].map((type) => (
							// Using label as the clickable element for better accessibility
							<motion.label
								key={type.id}
								htmlFor={`mount-${type.id}`} // Associate label with radio input
								whileHover={{ y: -3, boxShadow: "0 4px 10px rgba(0,0,0,0.05)" }} // Subtle lift and shadow
								transition={{ duration: 0.15 }}
								className={`relative p-4 border-2 rounded-xl cursor-pointer transition-all flex flex-col ${
									measure === type.id
										? "border-primary bg-primary/5 shadow-md"
										: "border-gray-200 hover:border-gray-300 bg-white"
								}`}
							>
								{/* Image Preview */}
								<div className="aspect-video rounded-md overflow-hidden mb-3 border border-gray-100 bg-gray-50">
									<img
										src={type.img}
										alt={type.label}
										className="w-full h-full object-contain"
									/>
								</div>
								{/* Radio Button and Text */}
								<div className="flex items-start gap-2 mt-auto pt-2">
									<input
										id={`mount-${type.id}`} // ID matches htmlFor
										aria-label={type.label} // Keep aria-label for redundancy
										type="radio"
										name="mount" // Group radio buttons
										value={type.id}
										checked={measure === type.id}
										onChange={(e) => setMeasure(e.target.value)}
										className="mt-1 w-4 h-4 text-primary focus:ring-primary/50 focus:ring-offset-1 flex-shrink-0" // Offset focus ring slightly
									/>
									<div className="flex-1">
										<span className="block font-medium text-sm leading-snug">
											{type.label}
										</span>
										<span className="block text-xs text-gray-600 leading-snug">
											{type.desc}
										</span>
									</div>
								</div>
							</motion.label>
						))}
					</div>
				</fieldset>
			</div>
		</Customize_Reworked>
	);
};

export default Customize_Details;
